import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { authService } from "../services/auth";
import { getCachedInstitutionData } from "../services/institutionDataCache";

const InstitutionContext = createContext();

export const useInstitution = () => {
  const context = useContext(InstitutionContext);
  if (!context) {
    throw new Error("useInstitution must be used within InstitutionProvider");
  }
  return context;
};

// Sanitize a custom dataset name so it can only map to a JSON file in /public
// (no path traversal, no slashes).
const sanitizeDatasetName = (name) =>
  (name || "").replace(/[^A-Za-z0-9_-]/g, "");

export const InstitutionProvider = ({ children }) => {
  const { id, name } = useParams();
  // For /custom/<name> the display id is the clean dataset name; for everything
  // else it's the route :id. basePath is the full URL prefix used for navigation.
  const isCustom = Boolean(name);
  const institutionId = isCustom ? sanitizeDatasetName(name) : id;
  const basePath = isCustom
    ? `/custom/${sanitizeDatasetName(name)}`
    : `/${id}`;
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(null);

  const handlePasswordSubmit = async (password) => {
    if (!institutionId) return;

    try {
      setIsLoading(true);
      setError(null);
      setLoadingProgress(null);

      await authService.verifyInstitution(institutionId, password);
      setIsAuthenticated(true);
      setShowPasswordPrompt(false);

      const cached = await getCachedInstitutionData(institutionId);
      if (cached?.sessions?.length) {
        setSessions(cached.sessions);
        setIsLoading(false);
      }

      const data = await authService.fetchAllInstitutionData(
        institutionId,
        (progress) => {
          setLoadingProgress(progress);
          setSessions(progress.currentData || []);
          if (progress.page === 1) {
            setIsLoading(false);
          }
        }
      );

      console.log("Institution data loaded:", {
        institutionId,
        sessionsCount: data.length,
      });

      setSessions(data);
      setIsLoading(false);
      setLoadingProgress(null);
    } catch (err) {
      console.error("Failed to load institution data:", err);
      setError(err.message);
      setIsAuthenticated(false);
      setShowPasswordPrompt(true);
      setIsLoading(false);
      setLoadingProgress(null);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (!institutionId) {
        setIsLoading(false);
        return;
      }

      // Static dataset loading:
      //   /custom/<name>  -> public/<name>.json
      if (isCustom) {
        const file = `/${institutionId}.json`;
        try {
          setIsLoading(true);
          const response = await fetch(file);
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const data = await response.json();
          setSessions(data);
          setIsAuthenticated(true);
          setShowPasswordPrompt(false);
          setIsLoading(false);
          console.log(`Static mode: loaded data from ${file}`);
        } catch (err) {
          console.error(`Failed to load static data from ${file}:`, err);
          setError(`Failed to load data from ${file}`);
          setIsLoading(false);
        }
        return;
      }

      if (authService.isInstitutionAuthenticated(institutionId)) {
        try {
          setIsLoading(true);
          setLoadingProgress(null);
          const cached = await getCachedInstitutionData(institutionId);
          if (cached?.sessions?.length) {
            setSessions(cached.sessions);
            setIsLoading(false);
          }
          const data = await authService.fetchAllInstitutionData(
            institutionId,
            (progress) => {
              setLoadingProgress(progress);
              setSessions(progress.currentData || []);
              if (progress.page === 1) {
                setIsLoading(false);
              }
            }
          );
          setSessions(data);
          setIsAuthenticated(true);
          setIsLoading(false);
          setLoadingProgress(null);
        } catch (err) {
          console.error("Failed to load institution data:", err);
          authService.logoutInstitution(institutionId);
          setError(err.message || "Failed to load institution data");
          setShowPasswordPrompt(true);
          setIsLoading(false);
          setLoadingProgress(null);
        }
      } else {
        setShowPasswordPrompt(true);
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [institutionId, isCustom]);

  return (
    <InstitutionContext.Provider
      value={{
        institutionId,
        basePath,
        sessions,
        isLoading,
        error,
        isAuthenticated,
        showPasswordPrompt,
        handlePasswordSubmit,
        loadingProgress,
      }}
    >
      {children}
    </InstitutionContext.Provider>
  );
};
